import { apiClient, handleApiError } from './client';
import {
  CelebrationCelebrant,
  CreateCelebrationCelebrantRequest,
  UpdateCelebrationCelebrantRequest
} from '@/types/user-contact.types';

class CelebrationCelebrantService {
  async getCelebrants(celebrationId: number): Promise<CelebrationCelebrant[]> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/celebrants`);
      
      // Handle backend response structure { success: true, data: [...] }
      let celebrantsData;
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        celebrantsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        // Fallback for direct array response
        celebrantsData = response.data;
      } else {
        celebrantsData = [];
      }
      
      // Ensure each celebrant has proper structure
      const processedCelebrants = celebrantsData.map((celebrant: any) => {
        // Ensure user_contact exists and has proper structure
        if (!celebrant.user_contact) {
          celebrant.user_contact = {
            id: celebrant.user_id || 0,
            full_name: 'Unknown Contact',
            email: '',
            phone: '',
            relationship: '',
            status: 'active'
          };
        }
        
        return celebrant;
      });
      
      return processedCelebrants;
    } catch (error: any) {
      console.error('Error fetching celebrants:', error);
      throw error;
    }
  }

  async getCelebrant(celebrationId: number, id: number): Promise<CelebrationCelebrant> {
    try {
      const response = await apiClient.get(`/celebrations/${celebrationId}/celebrants/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createCelebrant(celebrationId: number, data: CreateCelebrationCelebrantRequest): Promise<CelebrationCelebrant> {
    try {
      const response = await apiClient.post(`/celebrations/${celebrationId}/celebrants`, data);
      // Handle backend response structure { success: true, data: {...} }
      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      // Fallback for direct object response
      return response.data;
    } catch (error: any) {
      console.error('Error creating celebrant:', error);
      throw error; // Preserve the original error
    }
  }

  async updateCelebrant(celebrationId: number, id: number, data: UpdateCelebrationCelebrantRequest): Promise<CelebrationCelebrant> {
    try {
      const response = await apiClient.put(`/celebrations/${celebrationId}/celebrants/${id}`, data);
      return response.data;
    } catch (error: any) {
      console.error('Error updating celebrant:', error);
      throw error; // Preserve the original error
    }
  }

  async deleteCelebrant(celebrationId: number, id: number): Promise<void> {
    try {
      await apiClient.delete(`/celebrations/${celebrationId}/celebrants/${id}`);
    } catch (error: any) {
      console.error('Error deleting celebrant:', error);
      throw error; // Preserve the original error
    }
  }
}

export const celebrationCelebrantService = new CelebrationCelebrantService();
